import React, { useState } from "react";

import Modal from "../../../useOss/Modal";
import SelectServiceType from "./content/SelectServiceType";
import CreateAnAppFirst from "./content/CreateAnAppFirst";
import LicenseKeyModalContent from "../license-key-modal/LicenseKeyModalContent";
import GenerateLicenseKeyForManagedService from "./content/GenerateLicenceKeyForMangedService";
import { SassAppDevData, SassAppProductionData } from "../../../dashboard/saas/types";
import saasAnalytics from "../../../dashboard/saas/saasAnalytics";
import { usePaymentDetailsContext } from "../../../utils/payments";

export type PaidFeaturesModalSteps =
    | "choose-a-service"
    | "generate-licensekey-for-self-hosted"
    | "generate-licensekey-for-managed-service";

export type AppsListWithOnlyDeploymentsData = {
    appName: string;
    devDeployment?: SassAppDevData;
    prodDeployment?: SassAppProductionData;
}[];

export type EnablePaidFeaturesModalProps = {
    deploymentId: string | undefined;
    appId: string | undefined;
    appsList: AppsListWithOnlyDeploymentsData;
    isNewArchitecture: boolean;
};

export default function EnablePaidFeaturesModal({
    appId,
    deploymentId,
    appsList,
    isNewArchitecture
}: EnablePaidFeaturesModalProps) {
    const paymentDetailsContext = usePaymentDetailsContext();

    let defaultValue = false;

    if (paymentDetailsContext !== undefined && paymentDetailsContext.savedContext.action_type === "license-key") {
        defaultValue = true;
    }

    const [openModal, setIsOpenModal] = useState(defaultValue);

    function onCloseModal() {
        setIsOpenModal(false);
    }

    return (
        <>
            <button
                onClick={() => {
                    saasAnalytics.enablePaidFeaturesClick();
                    setIsOpenModal(true);
                }}
                className="btn btn-sm create-app-btn app-btn"
            >
                {isNewArchitecture ? "Enable / Disable paid features" : "Generate license key"}
            </button>
            {openModal ? (
                <Modal disableBackdropClose={true} show={openModal} modalClosed={onCloseModal}>
                    <EnablePaidFeatureModalContent
                        isNewArchitecture={isNewArchitecture}
                        onCloseModal={onCloseModal}
                        appId={appId}
                        appsList={appsList}
                        deploymentId={deploymentId}
                    />
                </Modal>
            ) : null}
        </>
    );
}

function EnablePaidFeatureModalContent({
    appId,
    appsList,
    deploymentId,
    isNewArchitecture,
    onCloseModal
}: EnablePaidFeaturesModalProps & { onCloseModal: () => void }) {
    const paymentDetailsContext = usePaymentDetailsContext();

    let defaultModalStep: PaidFeaturesModalSteps = "choose-a-service";

    if (paymentDetailsContext !== undefined && paymentDetailsContext.savedContext.action_type === "license-key") {
        defaultModalStep = "generate-licensekey-for-self-hosted";
    }

    const [currentPaidFeaturesModalStep, setCurrentPaidFeaturesModalStep] = useState<PaidFeaturesModalSteps>(
        defaultModalStep
    );

    if (isNewArchitecture === false) {
        return <LicenseKeyModalContent onCloseModal={onCloseModal} />;
    }

    switch (currentPaidFeaturesModalStep) {
        case "choose-a-service":
            return (
                <SelectServiceType
                    setCurrentPaidFeaturesModalStep={setCurrentPaidFeaturesModalStep}
                    onCloseModal={onCloseModal}
                />
            );
        case "generate-licensekey-for-self-hosted":
            return <LicenseKeyModalContent onCloseModal={onCloseModal} />;
        case "generate-licensekey-for-managed-service":
            if (appId === undefined || deploymentId === undefined) {
                return <CreateAnAppFirst onCloseModal={onCloseModal} />;
            }
            return (
                <GenerateLicenseKeyForManagedService
                    initiallySelectedApp={appId}
                    appsList={appsList}
                    deploymentId={deploymentId}
                    onCloseModal={onCloseModal}
                />
            );
        default:
            return (
                <SelectServiceType
                    setCurrentPaidFeaturesModalStep={setCurrentPaidFeaturesModalStep}
                    onCloseModal={onCloseModal}
                />
            );
    }
}
